/**
 * Actions de TanStack Query para el módulo de Auditoría
 *
 * @module AuditoriaActions
 * @description Hooks y funciones para consultar logs de auditoría
 * @author Sistema de Auditoría
 * @date 2025-12-22
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import type {
  AuditoriaLogDetalle,
  AuditoriaLogBitacora,
  AuditoriaFiltros,
  AuditoriaPaginatedResponse,
  VerificacionIntegridad,
  VerificacionLote,
  AuditoriaEstadisticas,
} from '@/shared/interfaces/erp/auditoria.interface';

const { get, post } = erpApi();

// ========================================
// TanStack Query Hooks
// ========================================

/**
 * Hook para obtener lista paginada de logs de auditoría
 */
export const useFetchAuditoriaLogs = (filtros?: AuditoriaFiltros, enabled = true) => {
  return useQuery({
    queryKey: ['AUDITORIA_LOGS', ...Object.values(filtros ?? {})],
    queryFn: async () => getAuditoriaLogs(filtros),
    enabled,
    staleTime: 30_000, // 30 segundos
  });
};

/**
 * Hook para obtener detalle de un log específico
 */
export const useFetchAuditoriaLogDetalle = (logId: string, enabled = true) => {
  return useQuery({
    queryKey: ['AUDITORIA_LOG_DETALLE', logId],
    queryFn: async () => getAuditoriaLogDetalle(logId),
    enabled: enabled && !!logId,
    staleTime: 60_000, // 1 minuto
  });
};

/**
 * Hook para obtener logs de un registro específico (bitácora)
 */
export const useFetchAuditoriaPorRegistro = (
  registroId: string,
  registroTipo?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ['AUDITORIA_POR_REGISTRO', registroId, registroTipo],
    queryFn: async () => getAuditoriaPorRegistro(registroId, registroTipo),
    enabled: enabled && !!registroId,
    staleTime: 30_000,
  });
};

/**
 * Hook para obtener estadísticas de auditoría
 */
export const useFetchAuditoriaEstadisticas = (dias: number = 30, enabled = true) => {
  return useQuery({
    queryKey: ['AUDITORIA_ESTADISTICAS', dias],
    queryFn: async () => getAuditoriaEstadisticas(dias),
    enabled,
    staleTime: 60_000, // 1 minuto
  });
};

/**
 * Hook para verificar integridad de un log
 */
export const useVerificarIntegridad = () => {
  return useMutation({
    mutationFn: verificarIntegridad,
  });
};

/**
 * Hook para verificar integridad de múltiples logs
 */
export const useVerificarLote = () => {
  return useMutation({
    mutationFn: verificarLote,
  });
};

// ========================================
// Funciones Axios
// ========================================

/**
 * Obtiene lista paginada de logs de auditoría
 */
export const getAuditoriaLogs = async (
  filtros?: AuditoriaFiltros
): Promise<AuditoriaPaginatedResponse> => {
  try {
    const queryParams = getUrlParamsFetch(filtros ?? {});
    const response = await get<AuditoriaPaginatedResponse>(
      `${urlBackend}api/v1/auditoria/?${queryParams}`,
      true
    );
    console.log('[DEBUG AUDITORIA] response completa:', response);
    console.log('[DEBUG AUDITORIA] response.data:', response?.data);
    // Determinar si la respuesta viene envuelta en ApiResponse o directamente
    if (response?.data && 'results' in response.data) {
      return response.data;
    }
    if (response && 'results' in (response as any)) {
      return response as unknown as AuditoriaPaginatedResponse;
    }
    // Fallback: retornar estructura vacía para evitar undefined
    return { count: 0, next: null, previous: null, results: [] };
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

/**
 * Obtiene detalle completo de un log (con datos desencriptados)
 */
export const getAuditoriaLogDetalle = async (logId: string): Promise<AuditoriaLogDetalle> => {
  try {
    const response = await get<AuditoriaLogDetalle>(`${urlBackend}api/v1/auditoria/${logId}/`, true);
    return response.data ?? (response as unknown as AuditoriaLogDetalle);
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

/**
 * Obtiene logs de auditoría de un registro específico (bitácora)
 */
export const getAuditoriaPorRegistro = async (
  registroId: string,
  registroTipo?: string
): Promise<AuditoriaLogBitacora[]> => {
  try {
    const params = new URLSearchParams({ registro_id: registroId });
    if (registroTipo) {
      params.append('registro_tipo', registroTipo);
    }

    const response = await get<AuditoriaLogBitacora[]>(
      `${urlBackend}api/v1/auditoria/por_registro/?${params.toString()}`,
      true
    );
    return response.data ?? (response as unknown as AuditoriaLogBitacora[]);
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

/**
 * Obtiene estadísticas de auditoría
 */
export const getAuditoriaEstadisticas = async (dias: number = 30): Promise<AuditoriaEstadisticas> => {
  try {
    const response = await get<AuditoriaEstadisticas>(
      `${urlBackend}api/v1/auditoria/estadisticas/?dias=${dias}`,
      true
    );
    return response.data ?? (response as unknown as AuditoriaEstadisticas);
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

/**
 * Verifica la integridad de un log
 */
export const verificarIntegridad = async (logId: string): Promise<VerificacionIntegridad> => {
  try {
    const response = await get<VerificacionIntegridad>(
      `${urlBackend}api/v1/auditoria/${logId}/verificar_integridad/`,
      true
    );
    return response.data ?? (response as unknown as VerificacionIntegridad);
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

/**
 * Verifica la integridad de múltiples logs (batch)
 */
export const verificarLote = async (logIds: string[]): Promise<VerificacionLote> => {
  try {
    const response = await post<VerificacionLote>(
      `${urlBackend}api/v1/auditoria/verificar_lote/`,
      { log_ids: logIds },
      true
    );
    return response.data ?? (response as unknown as VerificacionLote);
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

/**
 * Exporta logs a Excel (pendiente de implementación en backend)
 */
export const exportarAuditoriaExcel = async (filtros?: AuditoriaFiltros): Promise<Blob> => {
  try {
    const queryParams = getUrlParamsFetch(filtros ?? {});
    const response = await get<Blob>(
      `${urlBackend}api/v1/auditoria/export_excel/?${queryParams}`,
      true
    );
    return response.data ?? (response as unknown as Blob);
  } catch (error) {
    handleAxiosError(error);
    throw error;
  }
};

// ========================================
// Utilities
// ========================================

/**
 * Formatea la fecha de un log para mostrar
 */
export const formatFechaLog = (fecha: string): string => {
  const date = new Date(fecha);
  return new Intl.DateTimeFormat('es-EC', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

/**
 * Obtiene el color según el tipo de acción
 */
export const getAccionColor = (
  accion: string
): 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  const colors: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
    login: 'success',
    logout: 'warning',
    crear: 'success',
    actualizar: 'info',
    actualizar_parcial: 'info',
    eliminar: 'error',
    aprobar: 'success',
    rechazar: 'error',
    finalizar: 'primary',
    cancelar: 'warning',
    consultar: 'info',
    activar: 'success',
    desactivar: 'warning',
  };

  return colors[accion.toLowerCase()] || 'info';
};

/**
 * Obtiene el color según el método HTTP
 */
export const getMetodoHttpColor = (
  metodo: string
): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
  const colors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
    GET: 'info',
    POST: 'success',
    PUT: 'warning',
    PATCH: 'warning',
    DELETE: 'error',
  };

  return colors[metodo.toUpperCase()] || 'default';
};

/**
 * Obtiene una descripción amigable del módulo
 */
export const getNombreModulo = (modulo: string): string => {
  const nombres: Record<string, string> = {
    'autenticacion': 'Autenticación',
    'preventa.prospecto': 'Prospectos',
    'preventa.preventa': 'Preventas',
    'preventa.agendamiento': 'Agendamientos',
    'preventa.preagendamiento': 'Pre-Agendamientos',
    'preventa.orden_trabajo': 'Órdenes de Trabajo',
    'cliente': 'Clientes',
    'cliente.contrato': 'Contratos',
    'cliente.servicio': 'Servicios',
    'inventario': 'Inventario',
    'compras': 'Compras',
    'operaciones': 'Operaciones',
  };

  return nombres[modulo] || modulo;
};
